/**
 * @file       Document badge
 * @desc       Round clickable text tile for a document (file type
 *             initials or a short label).
 * @version    2.0.0
 * @copyright  Copyright (c) 2018-2026, MindShare HDV LLC.  All rights
 *             reserved.
 */

import
{
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ChangeDetectionStrategy,
  booleanAttribute,
  numberAttribute
} from '@angular/core';

@Component
({
    selector: 'document-badge',
    templateUrl: './documentBadge.component.html',
    styleUrls: ['./documentBadge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
/**
 * @desc   - Round text tile; shows initials of name, else value
 */
class DocumentBadgeComponent implements OnChanges
{
    @Input() name: string = '';
    @Input() value: string = '';
    @Input({ transform: numberAttribute }) size: number = 50;
    @Input({ transform: numberAttribute }) textSizeRatio: number = 3;
    @Input({ transform: numberAttribute }) initialsSize: number = 0;
    @Input({ transform: booleanAttribute }) round: boolean = true;
    @Input() bgColor: string = '';
    @Input() fgColor: string = '#FFF';
    @Input() badgeStyle: Record<string, string> = {};

    @Output() badgeClick = new EventEmitter<MouseEvent>();

    badgeText: string;
    containerStyle: Record<string, string>;
    contentStyle: Record<string, string>;

    /**
     * @desc   - component constructor
     * @access - public
     * @param  -
     * @return -
     */
    constructor()
    {
        this.badgeText = '';
        this.containerStyle = {};
        this.contentStyle = {};
    }

    /**
     * @desc   - Rebuild text and styles when inputs change
     * @access - public
     * @param  -
     * @return -
     */
    ngOnChanges()
    {
        let initials;

        initials = this.buildInitials();
        this.badgeText = initials || String(this.value || '');
        this.containerStyle =
        {
            width: this.size + 'px',
            height: this.size + 'px'
        };
        this.contentStyle =
        {
            textAlign: 'center',
            borderRadius: this.round ? '100%' : '0px',
            textTransform: 'uppercase',
            color: this.fgColor,
            backgroundColor: this.bgColor || 'transparent',
            font: Math.floor(this.size / this.textSizeRatio) +
                'px Helvetica, Arial, sans-serif',
            lineHeight: this.size + 'px',
            ...this.badgeStyle
        };
    }

    /**
     * @desc   - Emit badge click to the parent
     * @access - public
     * @param  - event : mouse click event
     * @return -
     */
    onClick
    (
        event: MouseEvent
    )
    {
        this.badgeClick.emit(event);
    }

    /**
     * @desc   - Upper-case first letter of each word in name
     * @access - private
     * @param  -
     * @return - initials, or empty string when name is blank
     */
    private buildInitials()
    {
        let words;
        let result;

        words = String(this.name || '').trim().split(' ');

        if (this.initialsSize > 0 && this.initialsSize < words.length)
        {
            words = words.slice(0, this.initialsSize);
        }

        result = words
            .filter((word: string) => word.length > 0)
            .map((word: string) => word[0].toUpperCase())
            .join('');

        return result;
    }
}
export { DocumentBadgeComponent }
